import React from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { ArcButton, ArcIconButton, ArcPagination, ArcSkeleton, ArcStack, ArcTable, ArcTableBody, ArcTableCell, ArcTableHead, ArcTableRow } from '@src/core/ui';
import { arcDateWithTime, arcDownloadFile, arcSecToMin, arcSpatium } from '@src/core/extensions';
import { PAGINATION_DEFAULT_SIZE, ZERO } from '@src/core/config';
import { useFlightSummaryAllMutation, useFlightSummaryByIdMutation, useGetDroneOrderHistoryQuery } from '@src/features/fleet/redux/api';
import { useUnitType } from '@src/core/features/settings';
import { trackError, toTrackedError } from '@src/core/sentry';
import { useSelectedFleetDrone } from '../../hooks';

const OrderHistoryPanel: React.FC = () => {
  // Subscribe so the totalDistance column (arcSpatium) re-renders on a live unit
  // switch (UNITS Standard R1).
  useUnitType();
  const [page, setPage] = React.useState(0);
  const { droneId } = useSelectedFleetDrone();
  const { data = { data: [], totalCount: ZERO }, isLoading, isFetching } = useGetDroneOrderHistoryQuery(
    droneId != null
      ? { id: droneId, page, size: PAGINATION_DEFAULT_SIZE }
      : skipToken
  );

  const [ flightSummaryAll ] = useFlightSummaryAllMutation();
  const [ flightSummaryById ] = useFlightSummaryByIdMutation();

  
  

  if (isLoading || isFetching) {
    return (
      <ArcStack spacing={1}>
        {Array(7).fill(1).map((_, idx) => {
          return <ArcSkeleton key={idx} height={40} />;
        })}
      </ArcStack>
    );
  }

  const onDownloadById = (id: number) => {
    flightSummaryById(id).unwrap().then((filePath) => {
      arcDownloadFile(filePath, `drone_order_${id}_history.csv`);
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0021', extra: { flow: 'flightSummaryById' } });
    });
  };

  const onDownloadAll = () => {
    flightSummaryAll().unwrap().then((filePath) => {
      arcDownloadFile(filePath, `all_history.csv`);
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0021', extra: { flow: 'flightSummaryAll' } });
    });
  };

  
  return (
    <ArcStack sx={{ overflowY: 'scroll' }} maxHeight='60vh'>
      <ArcStack direction='row' justifyContent='flex-end' marginLeft={'auto'} marginBottom={2} width='15.5%'>
        <ArcButton.brand onClick={onDownloadAll} fullWidth> Download All </ArcButton.brand>
      </ArcStack>
      <ArcTable>
        <ArcTableHead>
          <ArcTableRow>
            <ArcTableCell> Date </ArcTableCell>
            <ArcTableCell> Flight Time </ArcTableCell>
            <ArcTableCell> Distance </ArcTableCell>
            <ArcTableCell> Order # </ArcTableCell>
            <ArcTableCell> Flight Data</ArcTableCell>
          </ArcTableRow>
        </ArcTableHead>

        <ArcTableBody>
          {data.data.map((order) => {
            return (
              <ArcTableRow key={order.id}>
                <ArcTableCell> {arcDateWithTime(order.createDate)} </ArcTableCell>
                <ArcTableCell> {arcSecToMin(order.totalFlightTime)} </ArcTableCell>
                <ArcTableCell> {arcSpatium(order.totalDistance, { fromSi: true })} </ArcTableCell>
                <ArcTableCell> {order.id} </ArcTableCell>
                <ArcTableCell> 
                  <ArcIconButton onClick={() => onDownloadById(order.id)}> 
                    <DownloadForOfflineIcon />  
                  </ArcIconButton> 
                </ArcTableCell>
              </ArcTableRow>
            );
          })}
        </ArcTableBody>
      </ArcTable>
      {PAGINATION_DEFAULT_SIZE < data.totalCount &&
        (
          <ArcStack mt={2} alignItems='center'>
            <ArcPagination 
              page={page + 1} 
              count={Math.ceil(data.totalCount / PAGINATION_DEFAULT_SIZE)} 
              onChange={(_, page) => setPage(page - 1)} />
          </ArcStack>
        )
      }
    </ArcStack>
  );
};

export default OrderHistoryPanel;